import {
	createLandingSite,
	fetchLandingDomains,
	fetchLandingTemplates,
} from '@/api/landings';
import { ROUTES } from '@/app/routes';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import type { ILandingDomain, ILandingTemplate, LandingSiteMode } from '@/entities/landing.entity';
import { isValidLandingSubdomain } from '@/pages/landings/lib/landing-url';
import { templateRequiresSiteMode } from '@/pages/landings/lib/landing-site-mode';
import { GlobeIcon } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

interface AddSiteModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreated: () => void;
}

export default function AddSiteModal({ open, onOpenChange, onCreated }: AddSiteModalProps) {
	const navigate = useNavigate();
	const [domains, setDomains] = useState<ILandingDomain[]>([]);
	const [templates, setTemplates] = useState<ILandingTemplate[]>([]);
	const [domainId, setDomainId] = useState('');
	const [subdomain, setSubdomain] = useState('');
	const [templateSlug, setTemplateSlug] = useState('');
	const [siteMode, setSiteMode] = useState<LandingSiteMode>('apk');
	const [loadingMeta, setLoadingMeta] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [subdomainError, setSubdomainError] = useState('');

	const onlineDomains = useMemo(() => domains.filter(d => d.status === 'online'), [domains]);

	const selectedTemplate = templates.find(t => t.slug === templateSlug);
	const requiresSiteMode = templateRequiresSiteMode(selectedTemplate, templateSlug);
	const builtinTemplates = useMemo(
		() => templates.filter(t => t.source !== 'custom'),
		[templates],
	);
	const customApkTemplates = useMemo(
		() => templates.filter(t => t.source === 'custom' && t.templateKind !== 'telegram_auth'),
		[templates],
	);
	const customTelegramAuthTemplates = useMemo(
		() => templates.filter(t => t.templateKind === 'telegram_auth'),
		[templates],
	);

	useEffect(() => {
		if (requiresSiteMode) {
			setSiteMode('apk');
		}
	}, [templateSlug, requiresSiteMode]);

	const selectedDomain = onlineDomains.find(d => String(d.id) === domainId);
	const hostnamePreview =
		selectedDomain && subdomain.trim()
			? `${subdomain.trim().toLowerCase()}.${selectedDomain.zoneName}`
			: null;

	useEffect(() => {
		if (!open) return;
		let cancelled = false;

		async function loadMeta() {
			setLoadingMeta(true);
			try {
				const [domainItems, templateItems] = await Promise.all([fetchLandingDomains(), fetchLandingTemplates()]);
				if (cancelled) return;
				setDomains(domainItems);
				setTemplates(templateItems);
				const firstOnline = domainItems.find(d => d.status === 'online');
				if (firstOnline) setDomainId(String(firstOnline.id));
				if (templateItems.length > 0) setTemplateSlug(templateItems[0].slug);
			} finally {
				if (!cancelled) setLoadingMeta(false);
			}
		}

		void loadMeta();
		return () => {
			cancelled = true;
		};
	}, [open]);

	function handleOpenChange(next: boolean) {
		if (!next) {
			setSubdomain('');
			setSubdomainError('');
		}
		onOpenChange(next);
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		const parsedDomainId = Number(domainId);
		const trimmedSubdomain = subdomain.trim().toLowerCase();
		if (!Number.isFinite(parsedDomainId) || !trimmedSubdomain || !templateSlug) {
			toast.error('Заполните все поля');
			return;
		}
		if (!isValidLandingSubdomain(trimmedSubdomain)) {
			setSubdomainError('Только латиница, цифры и дефис');
			return;
		}
		if (requiresSiteMode && siteMode !== 'apk' && siteMode !== 'telegram') {
			toast.error('Выберите режим');
			return;
		}

		setSubdomainError('');
		setIsSaving(true);
		try {
			await createLandingSite({
				domainId: parsedDomainId,
				subdomain: trimmedSubdomain,
				templateSlug,
				...(templateSlug === 'telegram-auth'
					? { config: { telegramAuthEnabled: true } }
					: requiresSiteMode
						? { config: { mode: siteMode } }
						: {}),
			});
			toast.success('Сайт создан');
			setSubdomain('');
			onOpenChange(false);
			onCreated();
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className='sm:max-w-[440px]'>
				<DialogHeader>
					<DialogTitle>Создать сайт</DialogTitle>
					<DialogDescription>
						{hostnamePreview
							? `Сайт будет доступен по адресу ${hostnamePreview}`
							: 'Выберите домен, поддомен и шаблон лендинга.'}
					</DialogDescription>
				</DialogHeader>
				{loadingMeta ? (
					<div className='flex flex-col gap-4'>
						<Skeleton className='h-10 w-full' />
						<Skeleton className='h-10 w-full' />
						<Skeleton className='h-10 w-full' />
					</div>
				) : onlineDomains.length === 0 ? (
					<div className='flex flex-col items-center gap-3 py-4 text-center'>
						<div className='rounded-full bg-muted/60 p-3'>
							<GlobeIcon size={20} className='text-muted-foreground' />
						</div>
						<p className='text-sm text-muted-foreground max-w-[280px]'>
							Нет онлайн-доменов. Сначала добавьте домен и дождитесь активации DNS.
						</p>
						<Button
							type='button'
							variant='outline'
							size='sm'
							onClick={() => {
								handleOpenChange(false);
								navigate(ROUTES.landingsDomains);
							}}
						>
							Перейти к доменам
						</Button>
					</div>
				) : (
					<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
						<div className='flex flex-col gap-1.5'>
							<Label>Домен</Label>
							<Select value={domainId} onValueChange={setDomainId}>
								<SelectTrigger>
									<SelectValue placeholder='Выберите домен' />
								</SelectTrigger>
								<SelectContent>
									{onlineDomains.map(d => (
										<SelectItem key={d.id} value={String(d.id)}>
											{d.zoneName}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='landing-subdomain'>Поддомен</Label>
							<InputGroup>
								<InputGroupInput
									id='landing-subdomain'
									placeholder='click'
									value={subdomain}
									onChange={e => {
										setSubdomain(e.target.value);
										if (subdomainError) setSubdomainError('');
									}}
									disabled={isSaving}
									aria-invalid={Boolean(subdomainError)}
								/>
							</InputGroup>
							{subdomainError ? (
								<p className='text-xs text-destructive'>{subdomainError}</p>
							) : (
								<p className='text-xs text-muted-foreground'>Только латиница, цифры, дефис.</p>
							)}
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label>Шаблон</Label>
							<Select value={templateSlug} onValueChange={setTemplateSlug}>
								<SelectTrigger>
									<SelectValue placeholder='Выберите шаблон' />
								</SelectTrigger>
								<SelectContent>
									{builtinTemplates.length > 0 ? (
										<SelectGroup>
											<SelectLabel>Встроенные</SelectLabel>
											{builtinTemplates.map(t => (
												<SelectItem key={t.slug} value={t.slug}>
													{t.title}
												</SelectItem>
											))}
										</SelectGroup>
									) : null}
									{customApkTemplates.length > 0 ? (
										<SelectGroup>
											<SelectLabel>Мои APK шаблоны</SelectLabel>
											{customApkTemplates.map(t => (
												<SelectItem key={t.slug} value={t.slug}>
													{t.title}
												</SelectItem>
											))}
										</SelectGroup>
									) : null}
									{customTelegramAuthTemplates.length > 0 ? (
										<SelectGroup>
											<SelectLabel>Telegram Auth ZIP</SelectLabel>
											{customTelegramAuthTemplates.map(t => (
												<SelectItem key={t.slug} value={t.slug}>
													{t.title}
												</SelectItem>
											))}
										</SelectGroup>
									) : null}
								</SelectContent>
							</Select>
						</div>

						{requiresSiteMode ? (
							<div className='flex flex-col gap-1.5'>
								<Label>Режим</Label>
								<Select value={siteMode} onValueChange={value => setSiteMode(value as LandingSiteMode)}>
									<SelectTrigger>
										<SelectValue placeholder='Выберите режим' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='apk'>Скачивание APK</SelectItem>
										<SelectItem value='telegram'>Ссылка в Telegram</SelectItem>
									</SelectContent>
								</Select>
								<p className='text-xs text-muted-foreground'>
									{siteMode === 'telegram'
										? 'При создании ссылки укажите URL канала; клик по фото откроет Telegram.'
										: 'При создании ссылки выберите APK; клик по фото запустит скачивание.'}
								</p>
							</div>
						) : null}

						<DialogFooter className='flex-col sm:flex-col'>
							<div className='w-full'>
								<LoadingWrapper show={isSaving}>
									<Button type='submit' className='w-full' disabled={isSaving}>
										Создать
									</Button>
								</LoadingWrapper>
							</div>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}
